import { type ReactNode, useEffect, useState } from "react";
import { Button, Wordmark } from "@praesto/ui-kit";
import itLocale from "../locales/it.json";
import { applyEmailActionCode, inspectActionCode } from "../lib/auth-actions";
import { type ApplyCodeMode, loginHref } from "../lib/email-action";

const BUNDLE: Readonly<Record<string, string>> = itLocale;

/**
 * Standalone `t()` over `locales/it.json`: like `SignupPage`, this page renders
 * before (and without) any tenant boot, so `core.i18n` is not available.
 */
function t(key: string, params?: Readonly<Record<string, string>>): string {
  let value = BUNDLE[key] ?? key;
  if (params) {
    for (const [name, v] of Object.entries(params)) value = value.replaceAll(`{{${name}}}`, v);
  }
  return value;
}

/**
 * One run per `oobCode` for the page lifetime. The code is single-use, so the
 * StrictMode double-mounted effect must share the first run instead of calling
 * `applyActionCode` again (which would fail and show "invalid" after success).
 */
const runs = new Map<string, Promise<string>>();

function runAction(tenantId: string, oobCode: string): Promise<string> {
  let run = runs.get(oobCode);
  if (!run) {
    run = inspectActionCode(tenantId, oobCode).then(async (info) => {
      await applyEmailActionCode(tenantId, oobCode);
      return info.data.email ?? "";
    });
    runs.set(oobCode, run);
  }
  return run;
}

/** Test-only: forget memoised runs between cases. */
export function resetEmailActionRuns(): void {
  runs.clear();
}

/**
 * Email-action page for the non-reset Identity Platform modes, rendered
 * standalone (no auth) on `/auth/action`. It checks the code, applies it, and
 * says in plain words what changed on the account.
 */
export function EmailActionView({
  mode,
  oobCode,
  tenantId,
}: {
  mode: ApplyCodeMode;
  oobCode: string;
  tenantId: string | null;
}) {
  const [phase, setPhase] = useState<"working" | "done" | "invalid">("working");
  const [email, setEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    runAction(tenantId ?? "", oobCode)
      .then((mail) => {
        if (cancelled) return;
        setEmail(mail);
        setPhase("done");
      })
      .catch(() => {
        if (!cancelled) setPhase("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, oobCode]);

  if (phase === "working") {
    return (
      <ActionLayout>
        <p className="mt-6 text-caption text-fg-muted">{t("authAction.working")}</p>
      </ActionLayout>
    );
  }
  if (phase === "invalid") {
    return (
      <ActionMessage
        title={t("authAction.expired.title")}
        body={t("authAction.expired.body")}
        tenantId={tenantId}
      />
    );
  }
  return (
    <ActionMessage
      title={t(`authAction.${mode}.title`)}
      body={t(`authAction.${mode}.body`, { email })}
      tenantId={tenantId}
    />
  );
}

/** Error page for an `/auth/action` link that cannot be acted on at all. */
export function EmailActionErrorView({ reason }: { reason: "missing-code" | "unrecognized" }) {
  const key = reason === "missing-code" ? "authAction.missingCode" : "authAction.unrecognized";
  return <ActionMessage title={t(`${key}.title`)} body={t(`${key}.body`)} tenantId={null} />;
}

function ActionLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-canvas text-fg-default flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Wordmark height={34} />
        {children}
      </div>
    </div>
  );
}

function ActionMessage({
  title,
  body,
  tenantId,
}: {
  title: string;
  body: string;
  tenantId: string | null;
}) {
  return (
    <ActionLayout>
      <h1 className="mt-6 text-h3 font-semibold text-fg-strong">{title}</h1>
      <p className="mt-4 text-sm text-fg-muted leading-relaxed">{body}</p>
      <Button
        type="button"
        variant="primary"
        size="lg"
        fullWidth
        className="mt-8"
        onClick={() => window.location.assign(loginHref(tenantId))}
      >
        {t("authAction.goToLogin")}
      </Button>
    </ActionLayout>
  );
}
